import { useState } from "react";
import { runSelfTest, type SelfTestResult } from "../api";

// Operator setup guide + a live engine self-test. Mirrors docs/onboarding-runbook.md;
// keep the two in sync when steps change.
export function Setup() {
  return (
    <>
      <h2>Setup &amp; runbook</h2>
      <p className="muted">
        One-time per Serenity engineer, then once per client tenant. Full detail lives in{" "}
        <code className="mono">docs/onboarding-runbook.md</code>; this is the operational checklist.
      </p>

      <SelfTestPanel />

      <Section title="A. Deploy the Worker (one-time per environment)">
        <ol>
          <li>Authenticate wrangler (CF token / <code className="mono">wrangler login</code>).</li>
          <li>Run the resource + deploy helper — creates D1 + KV, applies every migration, deploys:</li>
        </ol>
        <Code>{`./scripts/deploy.sh        # creates D1 'sp-archive-db' + KV TENANT_SECRETS,
                           # applies migrations 0001–0007, deploys the Worker`}</Code>
        <p className="muted">Dashboard deploys separately (Cloudflare Pages, <code className="mono">dashboard/</code>).</p>
        <p>Seed the shared KV secrets (skip the per-tenant ones for now):</p>
        <Code>{`wrangler kv key put --binding=TENANT_SECRETS _admin_secret "<random>"
wrangler kv key put --binding=TENANT_SECRETS _wasabi_access_key "<key>"
wrangler kv key put --binding=TENANT_SECRETS _wasabi_secret_key "<secret>"
wrangler kv key put --binding=TENANT_SECRETS _aws_access_key "<key>"    # Glacier
wrangler kv key put --binding=TENANT_SECRETS _aws_secret_key "<secret>"
wrangler kv key put --binding=TENANT_SECRETS _graph_webhook_secret "<random>"`}</Code>
        <p className="muted">
          Set <code className="mono">WASABI_*</code> / <code className="mono">GLACIER_*</code> / <code className="mono">RETRIEVAL_BASE_URL</code> in <code className="mono">wrangler.toml</code>.
          Residency is enforced at boot (region allowlist + HTTPS).
        </p>
      </Section>

      <Section title="B. Smoke-test the deploy">
        <p>Use the panel above, or from a shell:</p>
        <Code>{`WORKER_URL=https://sp-archive.<acct>.workers.dev ADMIN_SECRET=<secret> ./scripts/smoke.sh`}</Code>
        <p className="muted">Proves chunk store + crypto + audit work on real Wasabi/D1/KEK — no tenant needed.</p>
      </Section>

      <Section title="C. Onboard a client tenant (per client)">
        <ol>
          <li><b>Entra app registration</b> in the client tenant (app-only Graph: <code className="mono">Sites.ReadWrite.All</code>, <code className="mono">Files.ReadWrite.All</code>); grant admin consent.</li>
          <li><b>Tenant row</b> in D1 (<code className="mono">tenants</code>) — or via the Tenants page.</li>
          <li><b>Per-tenant KEK</b> (32 random bytes) + version pointer in KV:
            <Code>{`KEK=$(openssl rand -base64 32)
wrangler kv key put --binding=TENANT_SECRETS "_kek_<tenantId>:1" "$KEK"
wrangler kv key put --binding=TENANT_SECRETS "_kek_<tenantId>_current" "1"`}</Code>
          </li>
          <li><b>Client secret</b> in KV: <code className="mono">tenant_&lt;tenantId&gt;</code>.</li>
          <li>Enable <b>Backup</b> (Tenants page) and <b>enrol a drive</b> → seeds an initial delta + Graph subscription.</li>
          <li>Set the <b>Archive</b> storage-limit policy; flip <code className="mono">archive_enabled</code> when ready.</li>
          <li>Optional: configure <b>Retention</b> (Glacier) + approver group on the Retention page.</li>
        </ol>
      </Section>

      <Section title="D. Ongoing integrity (automatic)">
        <ul>
          <li><b>Pre-delete read-back verify</b> — archive never deletes a source until its backup reconstructs and matches.</li>
          <li><b>Daily scrub</b> (rolling) + <b>weekly restore drill</b> (random) — verdicts in the <b>Audit</b> page (<code className="mono">integrity_checks</code>). A failed row = a backup that could not be reconstructed; investigate immediately.</li>
        </ul>
      </Section>
    </>
  );
}

function SelfTestPanel() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<SelfTestResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null); setRes(null);
    try { setRes(await runSelfTest()); }
    catch (e) { setErr(e instanceof Error ? e.message : "self-test failed"); }
    finally { setBusy(false); }
  }

  return (
    <section className="card" style={{ marginTop: 16, marginBottom: 8 }}>
      <div className="row spaced">
        <h3>Engine self-test</h3>
        <button className="btn" disabled={busy} onClick={run}>{busy ? "Running…" : "Run self-test"}</button>
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        Round-trips the chunk store + crypto + audit on real Wasabi/D1/KEK using a reserved tenant (cleans up after itself).
      </p>
      {err && <div className="err">{err}</div>}
      {res && (
        <>
          <div className={res.ok ? "ok" : "err"}>{res.ok ? "✓ PASS" : "✗ FAIL"}{res.error ? ` — ${res.error}` : ""}</div>
          {res.checks && (
            <ul className="mono" style={{ fontSize: 13, lineHeight: 1.6 }}>
              {res.checks.map((c) => (
                <li key={c.name}>{c.ok ? "✓" : "✗"} {c.name}{c.detail ? ` — ${c.detail}` : ""}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card" style={{ marginTop: 16 }}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="mono" style={{ background: "#1113", padding: "10px 12px", borderRadius: 6, overflowX: "auto", fontSize: 12.5, whiteSpace: "pre-wrap" }}>
      {children}
    </pre>
  );
}
